/**
 * Piloter › Valorisation au 31/12 (/piloter/valorisation) — valorisation PMP du stock
 * à une date d'arrêté, reconstituée depuis le journal, avec comparaison N−1 et exports.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, FileText, Sheet } from 'lucide-react';
import { toast } from 'sonner';
import { piloterApi } from '../../services/api';
import { usePublicMode } from '../../context/PublicModeContext';
import { formatEuro, formatInt } from '../../utils/format';
import { Card, Spinner, Button } from '../../components/ui/kit';
import { WineColorDot, MaskedValue } from '../../components/wine/wine';

/** Défaut : 31/12 de l'année en cours (arrêté comptable). */
function defaultCutoff() {
  return `${new Date().getFullYear()}-12-31`;
}

/** Évolution monétaire signée (+/−), colorée. */
function Evolution({ value, masked }) {
  if (masked) return <MaskedValue masked>•••</MaskedValue>;
  if (value === null || value === undefined) return <span className="text-n500">—</span>;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return <span className={value >= 0 ? 'text-gold700' : 'text-n700'}>{sign}{formatEuro(Math.abs(value))}</span>;
}

export default function Valorisation() {
  const navigate = useNavigate();
  const { isPublic } = usePublicMode();
  const [date, setDate] = useState(defaultCutoff());
  const [open, setOpen] = useState({});
  const [downloading, setDownloading] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['piloter-valorisation', date, isPublic],
    queryFn: () => piloterApi.valorisation({ date, public: isPublic }),
  });

  const doExport = async (kind) => {
    setDownloading(kind);
    try {
      await piloterApi.download(kind, date);
    } catch (e) {
      toast.error("Échec de l'export");
    } finally {
      setDownloading('');
    }
  };

  const prevYear = data ? new Date(data.prev_date).getFullYear() : '';

  return (
    <div className="p-4 md:p-8 max-w-[1000px] space-y-5">
      {/* Barre d'action : date d'arrêté, méthode, exports */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="kicker block mb-1">Date d'arrêté</label>
          <input type="date" className="vd-input h-9 w-[170px]" value={date} onChange={(e) => setDate(e.target.value)} data-testid="valo-date" />
        </div>
        {data && <p className="text-[13px] text-n600 pb-2">Méthode : <span className="text-ink">{data.method}</span></p>}
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" className="h-9" disabled={isPublic || downloading} onClick={() => doExport('pdf')} data-testid="export-pdf">
            <FileText className="w-4 h-4" /> {downloading === 'pdf' ? '…' : 'PDF comptable'}
          </Button>
          <Button variant="secondary" className="h-9" disabled={isPublic || downloading} onClick={() => doExport('xlsx')} data-testid="export-xlsx">
            <Sheet className="w-4 h-4" /> {downloading === 'xlsx' ? '…' : 'Excel'}
          </Button>
        </div>
      </div>
      {isPublic && <p className="text-[12px] text-n500">Exports désactivés en mode écran public.</p>}

      {isLoading || !data ? <Spinner /> : (
        <Card className="overflow-hidden" data-testid="valo-table">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="text-left kicker">
                <th className="py-3 px-4">Couleur</th>
                <th className="py-3 px-4 text-right">Bouteilles</th>
                <th className="py-3 px-4 text-right">Valeur d'achat</th>
                <th className="py-3 px-4 text-right">Valeur {prevYear}</th>
                <th className="py-3 px-4 text-right">Évolution</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <Fragment key={r.color}>
                  <tr className="hair-t hover:bg-surface/60 cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [r.color]: !o[r.color] }))} data-testid={`valo-row-${r.color}`}>
                    <td className="py-3 px-4">
                      <span className="flex items-center gap-2">
                        {open[r.color] ? <ChevronDown className="w-4 h-4 text-n500" /> : <ChevronRight className="w-4 h-4 text-n500" />}
                        <WineColorDot color={r.color} size={11} /> {r.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right nums">{formatInt(r.bottles)}</td>
                    <td className="py-3 px-4 text-right nums"><MaskedValue masked={isPublic}>{formatEuro(r.value)}</MaskedValue></td>
                    <td className="py-3 px-4 text-right nums text-n600"><MaskedValue masked={isPublic}>{formatEuro(r.value_prev)}</MaskedValue></td>
                    <td className="py-3 px-4 text-right nums"><Evolution value={r.evolution} masked={isPublic} /></td>
                  </tr>
                  {open[r.color] && r.detail.map((d) => (
                    <tr key={d.reference_id} className="hair-t bg-surface/40 text-[13px]" data-testid={`valo-detail-${d.reference_id}`}>
                      <td className="py-2 px-4 pl-12">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/cave/cuvee/${d.cuvee_id}`); }} className="text-n700 hover:text-gold800 transition-colors">
                          {d.name} <span className="text-n500">{d.year} · {d.format_label}</span>
                        </button>
                      </td>
                      <td className="py-2 px-4 text-right nums text-n600">{formatInt(d.quantity)}</td>
                      <td className="py-2 px-4 text-right nums text-n500" colSpan={2}>PMP <MaskedValue masked={isPublic}>{formatEuro(d.pmp)}</MaskedValue></td>
                      <td className="py-2 px-4 text-right nums text-n700"><MaskedValue masked={isPublic}>{formatEuro(d.value)}</MaskedValue></td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              <tr className="hair-t font-medium bg-gold100/40" data-testid="valo-total">
                <td className="py-3 px-4">Total</td>
                <td className="py-3 px-4 text-right nums">{formatInt(data.rows.reduce((s, r) => s + r.bottles, 0))}</td>
                <td className="py-3 px-4 text-right nums"><MaskedValue masked={isPublic}>{formatEuro(data.total)}</MaskedValue></td>
                <td className="py-3 px-4 text-right nums text-n600"><MaskedValue masked={isPublic}>{formatEuro(data.total_prev)}</MaskedValue></td>
                <td className="py-3 px-4 text-right nums"><Evolution value={data.evolution} masked={isPublic} /></td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
